import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { type DayCellFlags, dayCellVariants } from './variants'

export type DayCellProps = DayCellFlags & {
    /** The day number (or arbitrary label) rendered inside the cell. */
    day: number | string
    onPress?: () => void
    className?: string
    testID?: string
}

/**
 * Single calendar day cell (24×24). Presentational and fully controlled via
 * state flags — the owning {@link CalendarSheet} decides which flags apply.
 * Renders as a {@link Pressable} when `onPress` is supplied (unless disabled),
 * otherwise a static {@link Box}.
 */
export const DayCell: React.FC<DayCellProps> = ({
    day,
    onPress,
    className,
    testID,
    isDisabled,
    ...flags
}) => {
    const interactive = Boolean(onPress)
    const { container, text } = dayCellVariants({ ...flags, isDisabled, interactive })

    const content = (
        <Text size='P3' className={text} testID={testID ? `${testID}-text` : undefined}>
            {day}
        </Text>
    )

    if (interactive && !isDisabled) {
        return (
            <Pressable
                className={twMerge(container, className)}
                onPress={onPress}
                accessibilityRole='button'
                testID={testID}
            >
                {content}
            </Pressable>
        )
    }

    return (
        <Box className={twMerge(container, className)} testID={testID}>
            {content}
        </Box>
    )
}

DayCell.displayName = 'DayCell'
